import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { TextButton, useIsMounted, useOverlayContext, useStateInRef } from 'seagull';
import { useStore } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { selectors } from '~/store/state-selectors';
import { cardIdentity } from '~/utils/revenueOpportunities/opportunityCard';
import { dismissalRequests, dismissedToastText } from '~/utils/revenueOpportunities/opportunityDismissals';
import { useDismissRevenueOpportunity } from './hooks/useDismissRevenueOpportunity';
import { useUndoRevenueOpportunityDismissal } from './hooks/useUndoRevenueOpportunityDismissal';
const UNDO_BUTTON_LOOK = {
    weight: 'normal',
    underline: 'none',
    variant: 'paragraph1'
};
const UNDO_BI_NAME = 'pulse_opportunities_undo';
export const useRevenueOpportunityDismissals = (toastTestIds, view)=>{
    var _useStore;
    const { t: texts } = useLocaleKeys('ai.pulse');
    const { showInfoToast, showErrorToast } = useOverlayContext();
    const isMounted = useIsMounted();
    const businessId = (_useStore = useStore(selectors.user.businessId)) !== null && _useStore !== void 0 ? _useStore : '';
    const { dismissRevenueOpportunity } = useDismissRevenueOpportunity();
    const { undoRevenueOpportunityDismissal } = useUndoRevenueOpportunityDismissal();
    const [dismissingIdentities, setDismissingIdentities] = useState([]);
    const departures = useMemo(()=>new Set(), []);
    const getReleaseSettledDepartures = useStateInRef(releaseSettledDepartures);
    const shownKey = view.identities.join('\n');
    useEffect(()=>{
        getReleaseSettledDepartures()();
    }, [
        shownKey,
        view.isFetching
    ]);
    function releaseSettledDepartures() {
        const latest = view.read();
        departures.forEach((departure)=>{
            if (!latest.isFetching || !latest.identities.includes(departure.identity)) {
                departures.delete(departure);
                departure.release();
            }
        });
    }
    function waitUntilCardLeaves(identity) {
        return new Promise((release)=>{
            departures.add({
                identity,
                release
            });
            releaseSettledDepartures();
        });
    }
    async function undoDismissals(dismissalIds) {
        const failedCount = await undoRevenueOpportunityDismissal({
            dismissalIds,
            businessId
        });
        if (isMounted() && failedCount > 0) {
            showErrorToast(texts.section.toasts.undoFailed(), toastTestIds.UndoFailed());
        }
    }
    function undoAction(onUndo, biData) {
        const bi = {
            name: UNDO_BI_NAME,
            data: biData
        };
        return /*#__PURE__*/ _jsx(TextButton, _object_spread_props(_object_spread({}, UNDO_BUTTON_LOOK), {
            bi: bi,
            testId: toastTestIds.Undo(),
            onClick: onUndo,
            children: texts.section.toasts.undo()
        }));
    }
    function showDismissedToast(option, undoIds, biData) {
        const undo = ()=>{
            closeToast();
            void undoDismissals(undoIds);
        };
        const closeToast = showInfoToast(dismissedToastText(option, texts), toastTestIds.Dismissed(), {
            action: undoIds.length > 0 ? undoAction(undo, biData) : undefined
        });
    }
    function reportDismissal(option, settled, biData) {
        const { undoIds, dismissedCount, failedCount } = settled;
        if (failedCount > 0) {
            showErrorToast(texts.section.toasts.dismissFailed(), toastTestIds.DismissFailed());
        }
        if (dismissedCount > 0) {
            showDismissedToast(option, undoIds, biData);
        }
    }
    function lockCard(card) {
        const identity = cardIdentity(card);
        setDismissingIdentities((current)=>[
                ...current,
                identity
            ]);
        return identity;
    }
    function unlockCard(identity) {
        setDismissingIdentities((current)=>current.filter((pending)=>pending !== identity));
    }
    const dismissCard = async (card, option, biData)=>{
        const identity = lockCard(card);
        const settled = await dismissRevenueOpportunity({
            requests: dismissalRequests(card, option),
            businessId
        });
        if (settled.dismissedCount > 0) {
            await waitUntilCardLeaves(identity);
        }
        if (isMounted()) {
            unlockCard(identity);
            reportDismissal(option, settled, biData);
        }
    };
    return {
        isDismissing: (identity)=>dismissingIdentities.includes(identity),
        dismissCard
    };
};
